/**
 * HaynesPro work-order labor time API (#1442 / #2280).
 *
 * Mirrors staff-api haynespro-workorder routes. GET is capability + active
 * snapshot only (no vendor call). Browse / preview / adopt are user-triggered.
 * The client never supplies VIN, VRID, hours, or price.
 */

import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api';
import { queryKeys } from './queryKeys';
import type { ConfirmedHaynesProVariant } from './useVehicleTechnicalData';
import type { BookingServiceLaborItem } from './useWorkOrders';

export const HAYNESPRO_CAR_TYPE_GROUPS = [
  'ENGINE',
  'TRANSMISSION',
  'STEERING',
  'BRAKES',
  'EXTERIOR',
  'ELECTRONICS',
  'QUICKGUIDES',
] as const;

export type HaynesProCarTypeGroup = (typeof HAYNESPRO_CAR_TYPE_GROUPS)[number];

export const WORKORDER_TIME_ERROR_CODES = [
  'disabled',
  'snapshots_disabled',
  'tenant_not_licensed',
  'expired_credentials',
  'invalid_config',
  'work_order_not_found',
  'vehicle_not_found',
  'labor_item_not_found',
  'variant_required',
  'stale_fingerprint',
  'stale_revision',
  'stale_selection',
  'stale_labor',
  'preview_expired',
  'preview_invalid',
  'confirmation_required',
  'manual_override',
  'overlap_blocked',
  'billing_locked',
  'fixed_service_labor_locked',
  'unsupported_conditions',
  'hours_overflow',
  'path_invalid',
  'leaf_required',
  'node_not_found',
  'too_many_nodes',
  'invalid_group',
  'no_data',
  'operation_rights',
  'provider_auth',
  'provider_ban',
  'provider_error',
] as const;

export type WorkOrderTimeErrorCode = (typeof WORKORDER_TIME_ERROR_CODES)[number];

export type HaynesProAvailabilityReason =
  | 'ok'
  | 'disabled'
  | 'tenant_not_licensed'
  | 'expired_credentials'
  | 'invalid_config';

export type RepairTreeUnsupportedReason = 'time_criterias' | 'time_unknown' | 'hours_overflow';

export interface HaynesProRepairTreeNode {
  id: string;
  description: string | null;
  hasSubnodes: boolean;
  order: number | null;
  hundredths: number | null;
  decimalHours: number | null;
  presentationMinutes: number | null;
  selectable: boolean;
  oeCode: string | null;
  unsupportedReason: RepairTreeUnsupportedReason | null;
}

export interface HaynesProIncludedSubtask {
  role: 'included';
  awNumber: string | null;
  description: string | null;
  hundredths: number | null;
  billable: false;
  displayAsExtraTime: false;
}

export interface HaynesProSelectableSubtask {
  role: 'preliminary' | 'follow_up';
  awNumber: string;
  description: string | null;
  hundredths: number | null;
  selected: boolean;
  billable: boolean;
  displayAsExtraTime: boolean;
}

export interface HaynesProTimeSnapshot {
  id: string;
  bookingId: string;
  laborItemId: string | null;
  variantId: string;
  variantRevision: number;
  provider: 'haynespro';
  carTypeId: number;
  repairtimeTypeId: number;
  typeCategory: string;
  workpositionId: string;
  carTypeGroup: string;
  locale: string;
  fetchedAt: string;
  dataVersion: string | null;
  acceptedBy: string;
  acceptedAt: string;
  originalHours: number;
  hundredths: number;
  sourceFingerprint: string;
  revision: number;
}

export interface HaynesProWorkOrderTimeCapability {
  available: boolean;
  reason: HaynesProAvailabilityReason | string;
  snapshotsEnabled: boolean;
  confirmedVariant: ConfirmedHaynesProVariant | null;
  currentSourceFingerprint: string;
  isStale: boolean;
  activeSnapshot: HaynesProTimeSnapshot | null;
}

export interface HaynesProBrowseBody {
  carTypeGroup: HaynesProCarTypeGroup;
  nodeId?: string;
  expectedSourceFingerprint: string;
  expectedCurrentVariantRevision: number;
}

export interface HaynesProBrowseResult {
  parentNodeId: string;
  carTypeGroup: HaynesProCarTypeGroup;
  language: 'fi' | 'en';
  nodes: readonly HaynesProRepairTreeNode[];
  confirmedVariant: ConfirmedHaynesProVariant;
  currentSourceFingerprint: string;
}

export interface HaynesProPreviewBody {
  bookingServiceId: string;
  laborItemId: string;
  carTypeGroup: HaynesProCarTypeGroup;
  path: readonly string[];
  expectedSourceFingerprint: string;
  expectedCurrentVariantRevision: number;
}

export interface HaynesProPreviewResult {
  laborHours: number;
  hundredths: number;
  presentationMinutes: number;
  node: { id: string; description: string | null };
  included: readonly HaynesProIncludedSubtask[];
  preliminary: readonly HaynesProSelectableSubtask[];
  followUp: readonly HaynesProSelectableSubtask[];
  extraBillableDecimalHours: 0;
  previewToken: string;
  previewExpiresAt: string;
  variantRevision: number;
  sourceFingerprint: string;
}

export interface HaynesProAdoptBody {
  confirmed: true;
  previewToken: string;
  bookingServiceId: string;
  laborItemId: string;
  replaceManualOverride?: boolean;
}

export interface HaynesProAdoptResult {
  laborItem: BookingServiceLaborItem;
  snapshot: HaynesProTimeSnapshot;
  estimatedDurationHours: number | null;
  idempotent: boolean;
}

export interface ParsedWorkOrderTimeError {
  code: WorkOrderTimeErrorCode | 'unknown';
  status: number;
  currentSourceFingerprint?: string;
  retryAfterSeconds?: number;
  currentRevision?: number;
}

export const haynesProWorkOrderTimeKeys = {
  capability: (workOrderId: string) =>
    [...queryKeys.workOrders.detail(workOrderId), 'haynespro', 'repair-times'] as const,
};

const ERROR_CODE_SET = new Set<string>(WORKORDER_TIME_ERROR_CODES);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parseWorkOrderTimeError(error: unknown): ParsedWorkOrderTimeError {
  if (error instanceof ApiError) {
    const data = error.data;
    if (isRecord(data) && typeof data.error === 'string' && ERROR_CODE_SET.has(data.error)) {
      const parsed: ParsedWorkOrderTimeError = {
        code: data.error as WorkOrderTimeErrorCode,
        status: error.status,
      };
      if (typeof data.currentSourceFingerprint === 'string') {
        parsed.currentSourceFingerprint = data.currentSourceFingerprint;
      }
      if (typeof data.retryAfterSeconds === 'number') {
        parsed.retryAfterSeconds = data.retryAfterSeconds;
      }
      if (typeof data.currentRevision === 'number') {
        parsed.currentRevision = data.currentRevision;
      }
      return parsed;
    }
    return { code: 'unknown', status: error.status };
  }
  return { code: 'unknown', status: 0 };
}

export function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

const STALE_CODES: ReadonlySet<WorkOrderTimeErrorCode | 'unknown'> = new Set([
  'stale_fingerprint',
  'stale_revision',
  'stale_selection',
  'stale_labor',
]);

export function isStaleWorkOrderTimeError(code: WorkOrderTimeErrorCode | 'unknown'): boolean {
  return STALE_CODES.has(code);
}

function repairTimesPath(workOrderId: string, suffix = ''): string {
  return `/work-orders/${workOrderId}/haynespro/repair-times${suffix}`;
}

export function workOrderTimeInvalidationKeys(workOrderId: string) {
  return {
    workOrderDetail: queryKeys.workOrders.detail(workOrderId),
    workOrderHistory: queryKeys.workOrders.history(workOrderId),
    workOrderLists: queryKeys.workOrders.lists(),
    workOrderStats: queryKeys.workOrders.stats(),
    bookingsAll: queryKeys.bookings.all,
    invoicePreview: ['invoicePreview', workOrderId] as const,
    capability: haynesProWorkOrderTimeKeys.capability(workOrderId),
  };
}

function invalidateWorkOrderQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  workOrderId: string,
): void {
  const keys = workOrderTimeInvalidationKeys(workOrderId);
  void queryClient.invalidateQueries({ queryKey: keys.workOrderDetail });
  void queryClient.invalidateQueries({ queryKey: keys.workOrderHistory });
  void queryClient.invalidateQueries({ queryKey: keys.workOrderLists });
  void queryClient.invalidateQueries({ queryKey: keys.workOrderStats });
  void queryClient.invalidateQueries({ queryKey: keys.bookingsAll });
  void queryClient.invalidateQueries({ queryKey: keys.invoicePreview });
  void queryClient.invalidateQueries({ queryKey: keys.capability });
}

export function bookingIdForAdoptInvalidation(
  result: Pick<HaynesProAdoptResult, 'snapshot'>,
  mutationTargetId: string | undefined,
): string | undefined {
  const snapshotBookingId = result.snapshot?.bookingId;
  if (typeof snapshotBookingId === 'string' && snapshotBookingId.length > 0) {
    return snapshotBookingId;
  }
  if (typeof mutationTargetId === 'string' && mutationTargetId.length > 0) {
    return mutationTargetId;
  }
  return undefined;
}

export function useHaynesProWorkOrderTime(workOrderId: string | undefined) {
  const queryClient = useQueryClient();
  const browseAbortRef = useRef<AbortController | null>(null);
  const previewAbortRef = useRef<AbortController | null>(null);
  const adoptAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      browseAbortRef.current?.abort();
      previewAbortRef.current?.abort();
      browseAbortRef.current = null;
      previewAbortRef.current = null;
    };
  }, [workOrderId]);

  const capabilityQuery = useQuery({
    queryKey: haynesProWorkOrderTimeKeys.capability(workOrderId ?? ''),
    queryFn: () =>
      api.get<HaynesProWorkOrderTimeCapability>(repairTimesPath(workOrderId ?? '')),
    enabled: Boolean(workOrderId),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const browseMutation = useMutation({
    mutationFn: (body: HaynesProBrowseBody) => {
      if (!workOrderId) {
        return Promise.reject(new Error('workOrderId is required'));
      }
      browseAbortRef.current?.abort();
      const controller = new AbortController();
      browseAbortRef.current = controller;
      return api.post<HaynesProBrowseResult>(repairTimesPath(workOrderId), body, {
        signal: controller.signal,
      });
    },
  });

  const previewMutation = useMutation({
    mutationFn: (body: HaynesProPreviewBody) => {
      if (!workOrderId) {
        return Promise.reject(new Error('workOrderId is required'));
      }
      previewAbortRef.current?.abort();
      const controller = new AbortController();
      previewAbortRef.current = controller;
      return api.post<HaynesProPreviewResult>(
        repairTimesPath(workOrderId, '/preview'),
        body,
        { signal: controller.signal },
      );
    },
  });

  const adoptMutation = useMutation({
    mutationFn: (body: HaynesProAdoptBody) => {
      const targetWorkOrderId = workOrderId;
      if (!targetWorkOrderId) {
        return Promise.reject(new Error('workOrderId is required'));
      }
      adoptAbortRef.current?.abort();
      const controller = new AbortController();
      adoptAbortRef.current = controller;
      return api
        .post<HaynesProAdoptResult>(repairTimesPath(targetWorkOrderId, '/adopt'), body, {
          signal: controller.signal,
        })
        .then((result) => ({ result, targetWorkOrderId }));
    },
    onSuccess: (payload) => {
      const bookingId = bookingIdForAdoptInvalidation(payload.result, payload.targetWorkOrderId);
      if (!bookingId) return;
      invalidateWorkOrderQueries(queryClient, bookingId);
    },
  });

  return {
    capabilityQuery,
    browseMutation,
    previewMutation,
    adoptMutation,
  };
}
